import { useContext, useEffect, useState } from 'react';
import {
  Checkbox,
  DefaultButton,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Spinner,
  Stack,
  Text,
  TextField,
} from '@fluentui/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import { getApiErrorMessage } from '@moko/utils';
import AppContext from '../AppContext';
import { successMessageAtom } from '../../atoms/messageBarAtoms';
import {
  saveAutomaticSurcharge,
  type ISaveAutomaticSurcharge,
} from '../../services/automaticSurchargeService';
import { automaticSurchargesPath } from './pricingSettings';
import { loadSurchargeRows } from './AutomaticSurcharges';

const toInput = (row: {
  id: number;
  enabled: boolean;
  countryCode: string | null;
  stateCode: string | null;
}): ISaveAutomaticSurcharge => ({
  pricingItemId: row.id,
  enabled: row.enabled,
  countryCode: row.countryCode,
  stateCode: row.stateCode,
});

const AutomaticSurchargeForm = () => {
  const navigate = useNavigate();
  const query = new URLSearchParams(useLocation().search);
  const pricingItemId = Number.parseInt(query.get('id') ?? '');
  const validId = !Number.isNaN(pricingItemId) && pricingItemId > 0;
  const setSuccessMessage = useSetAtom(successMessageAtom);
  const context = useContext(AppContext);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [rule, setRule] = useState<ISaveAutomaticSurcharge>();
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    context.setSelectedTab('Global settings');
  }, []);

  useEffect(() => {
    if (!validId) {
      setError('This surcharge could not be found.');
      setLoaded(true);
      return;
    }
    const controller = new AbortController();
    setLoaded(false);
    setError(undefined);
    loadSurchargeRows(controller)
      .then((rows) => {
        if (controller.signal.aborted) return;
        const row = rows.find((item) => item.id === pricingItemId);
        if (!row) {
          setError('This surcharge could not be found.');
          setLoaded(true);
          return;
        }
        setName(row.name);
        setCode(row.code);
        setRule(toInput(row));
        setLoaded(true);
      })
      .catch((e) => {
        if (controller.signal.aborted) return;
        setError(getApiErrorMessage(e, 'Unable to load surcharge defaults.'));
        setLoaded(true);
      });
    return () => controller.abort();
  }, [pricingItemId, validId]);

  const change = (patch: Partial<ISaveAutomaticSurcharge>) => {
    setRule((current) => (current ? { ...current, ...patch } : current));
    setError(undefined);
  };

  const geographic = !!rule && rule.countryCode !== null;
  const valid =
    !!rule &&
    rule.pricingItemId > 0 &&
    (!geographic || /^[A-Za-z]{2}$/.test(rule.countryCode ?? '')) &&
    (!rule.stateCode || /^[A-Za-z0-9]{1,10}$/.test(rule.stateCode));

  const save = async () => {
    if (!rule || !valid) return;
    const controller = new AbortController();
    setSaving(true);
    setError(undefined);
    try {
      await saveAutomaticSurcharge(controller, {
        pricingItemId: rule.pricingItemId,
        enabled: rule.enabled,
        countryCode: rule.countryCode?.trim().toUpperCase() || null,
        stateCode: rule.stateCode?.trim().toUpperCase() || null,
      });
      setSuccessMessage('Surcharge default saved.');
      navigate(automaticSurchargesPath);
    } catch (e) {
      if (!controller.signal.aborted)
        setError(getApiErrorMessage(e, 'Unable to save surcharge default.'));
      setSaving(false);
    }
  };

  const back = () => navigate(automaticSurchargesPath);

  return (
    <div style={{ width: '100%', maxWidth: 640 }}>
      <Stack tokens={{ childrenGap: 16 }}>
        <Text variant="xLarge">Edit automatic surcharge</Text>
        {!loaded && !error && <Spinner label="Loading surcharge default…" />}
        {error && (
          <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>
        )}
        {loaded && !rule && <DefaultButton text="Cancel" onClick={back} />}
        {rule && (
          <>
            <Text>
              {name} ({code})
            </Text>
            <Stack horizontal tokens={{ childrenGap: 8 }}>
              <PrimaryButton
                text={saving ? 'Saving…' : 'Save'}
                disabled={saving || !valid}
                onClick={() => {
                  void save();
                }}
              />
              <DefaultButton text="Cancel" disabled={saving} onClick={back} />
            </Stack>
            <Checkbox
              label="Enabled by default"
              checked={rule.enabled}
              disabled={saving}
              onChange={(_, checked) => change({ enabled: !!checked })}
            />
            <Text>
              Tenants using the global setting follow this default. Explicit
              tenant overrides take precedence, including when the global
              default is disabled.
            </Text>
            <Checkbox
              label="Restrict to Site location"
              checked={geographic}
              disabled={saving}
              onChange={(_, checked) =>
                change({
                  countryCode: checked ? '' : null,
                  stateCode: null,
                })
              }
            />
            {geographic && (
              <>
                <TextField
                  label="Country code"
                  required
                  value={rule.countryCode ?? ''}
                  maxLength={2}
                  description="Two-letter country code, for example US."
                  disabled={saving}
                  onChange={(_, value) => change({ countryCode: value ?? '' })}
                  errorMessage={
                    !/^[A-Za-z]{2}$/.test(rule.countryCode ?? '')
                      ? 'Enter a two-letter country code.'
                      : undefined
                  }
                />
                <TextField
                  label="State / province code"
                  value={rule.stateCode ?? ''}
                  maxLength={10}
                  description="Optional. For California use country US and state CA."
                  disabled={saving}
                  onChange={(_, value) => change({ stateCode: value || null })}
                />
                <Text>
                  Site conditions apply to every enabled tenant, including
                  tenant overrides. The surcharge applies once when any linked
                  Site matches.
                </Text>
              </>
            )}
          </>
        )}
      </Stack>
    </div>
  );
};

export default AutomaticSurchargeForm;
